import {
  BUDGET_OPTIONS,
  MOVE_IN_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  ROOM_TYPE_OPTIONS,
  type BudgetId,
  type MoveInId,
  type PropertyTypeId,
  type RoomTypeId,
} from "@/config/intake";
import { IDENTITY_CARDS } from "@/data/identities";
import { getGuide } from "@/lib/content";
import { CRITERION_OPTIONS, type CriterionId, type Region } from "@/types/content";
import type { IdentityId, IntakeSchoolId, SchoolId } from "@/types/navigation";
import { IDENTITY_IDS, INTAKE_SCHOOL_IDS, SCHOOL_IDS, SCHOOL_LABELS } from "@/types/navigation";

export type IntakeFormData = {
  displayName: string;
  phone: string;
  school: IntakeSchoolId | "";
  schoolName: string;
  identity: Exclude<IdentityId, "other"> | "";
  regions: string[];
  budget: BudgetId | "";
  roomTypes: RoomTypeId | "";
  propertyTypes: PropertyTypeId[];
  moveIn: MoveInId | "";
  note: string;
  criterion?: CriterionId | null;
  communityId?: string;
  communityNameEn?: string;
  unitId?: string;
  source?: string;
};

export type ValidatedIntakeForm = IntakeFormData & {
  displayName: string;
  phone: string;
  school: IntakeSchoolId;
  schoolName: string;
  identity: Exclude<IdentityId, "other">;
  budget: BudgetId;
  roomTypes: RoomTypeId;
  moveIn: MoveInId;
};

export type IntakeValidationResult =
  | { ok: true; data: ValidatedIntakeForm }
  | { ok: false; message: string };

const budgetSet = new Set(BUDGET_OPTIONS.map((o) => o.id));
const roomSet = new Set(ROOM_TYPE_OPTIONS.map((o) => o.id));
const moveInSet = new Set(MOVE_IN_OPTIONS.map((o) => o.id));
const schoolSet = new Set<string>(INTAKE_SCHOOL_IDS);
const knownSchoolSet = new Set<string>(SCHOOL_IDS);
const identitySet = new Set(IDENTITY_IDS.filter((id) => id !== "other"));

function normalizePhone(raw: string): string | null {
  const trimmed = raw.trim();
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length < 8 || digits.length > 15) return null;
  return trimmed.startsWith("+") ? `+${digits}` : digits;
}

export function resolveRegionOptions(
  school: IntakeSchoolId | "",
  identity: Exclude<IdentityId, "other"> | "",
  preset: Region[],
): Region[] {
  if (school === "other") return [];
  if (preset.length > 0) return preset;
  if (!school || !identity || !knownSchoolSet.has(school)) return [];
  return getGuide(school as SchoolId, identity)?.regions ?? [];
}

function schoolSummaryLabel(data: ValidatedIntakeForm): string {
  if (data.school === "other") return data.schoolName;
  return SCHOOL_LABELS[data.school];
}

export function validateIntakeForm(data: IntakeFormData): IntakeValidationResult {
  const name = data.displayName.trim();
  if (!name) {
    return { ok: false, message: "请填写称呼，方便顾问对上号" };
  }
  if (name.length > 20) {
    return { ok: false, message: "称呼不超过 20 字" };
  }
  const phone = normalizePhone(data.phone);
  if (!phone) {
    return { ok: false, message: "请填写有效的手机号" };
  }
  if (!data.school || !schoolSet.has(data.school)) {
    return { ok: false, message: "请选择学校" };
  }
  const schoolName = data.schoolName.trim();
  if (data.school === "other") {
    if (!schoolName) {
      return { ok: false, message: "请填写学校名称" };
    }
    if (schoolName.length > 64) {
      return { ok: false, message: "学校名称不超过 64 字" };
    }
  }
  if (!data.identity || !identitySet.has(data.identity)) {
    return { ok: false, message: "请选择身份" };
  }
  if (data.school !== "other" && data.regions.length === 0) {
    return { ok: false, message: "请选择意向片区" };
  }
  if (!data.budget || !budgetSet.has(data.budget)) {
    return { ok: false, message: "请选择月预算" };
  }
  if (!data.roomTypes || !roomSet.has(data.roomTypes)) {
    return { ok: false, message: "请选择户型" };
  }
  if (data.propertyTypes.length === 0) {
    return { ok: false, message: "请选择房源类型" };
  }
  if (data.propertyTypes.includes("any") && data.propertyTypes.length > 1) {
    return { ok: false, message: "「都可以」不能与其他选项同时选择" };
  }
  if (!data.moveIn || !moveInSet.has(data.moveIn)) {
    return { ok: false, message: "请选择入住时间" };
  }
  if (data.note.length > 200) {
    return { ok: false, message: "补充说明不超过 200 字" };
  }
  return {
    ok: true,
    data: {
      ...data,
      displayName: name,
      phone,
      school: data.school,
      schoolName: data.school === "other" ? schoolName : "",
      identity: data.identity,
      budget: data.budget,
      roomTypes: data.roomTypes,
      moveIn: data.moveIn,
    },
  };
}

function labelFor<T extends { id: string; label: string }>(options: readonly T[], id: string): string {
  return options.find((o) => o.id === id)?.label ?? id;
}

export function formatLeadSummary(data: ValidatedIntakeForm, regions: Region[]): string {
  const regionMap = Object.fromEntries(regions.map((r) => [r.id, r.nameCn]));
  const regionText = data.regions.map((id) => regionMap[id] ?? id).join("、");
  const identityTitle =
    IDENTITY_CARDS.find((c) => c.id === data.identity)?.title ?? data.identity;
  const propertyText = data.propertyTypes
    .map((id) => labelFor(PROPERTY_TYPE_OPTIONS, id))
    .join("、");

  const lines = [
    "【留学管家 · 需求摘要】",
    `称呼：${data.displayName}`,
    `手机：${data.phone}`,
    `学校：${schoolSummaryLabel(data)}`,
    `身份：${identityTitle}`,
  ];

  if (data.regions.length > 0) {
    lines.push(`片区：${regionText}`);
  }

  lines.push(
    `预算：${labelFor(BUDGET_OPTIONS, data.budget)}`,
    `户型：${labelFor(ROOM_TYPE_OPTIONS, data.roomTypes)}`,
    `房源类型：${propertyText}`,
    `入住：${labelFor(MOVE_IN_OPTIONS, data.moveIn)}`,
  );

  if (data.criterion) {
    const criterionTitle = CRITERION_OPTIONS.find((o) => o.id === data.criterion)?.title;
    if (criterionTitle) lines.push(`首要标准：${criterionTitle}`);
  }
  if (data.communityNameEn) {
    lines.push(`小区：${data.communityNameEn}`);
  }
  const note = data.note.trim();
  if (note) lines.push(`补充：${note}`);

  return lines.join("\n");
}

export function buildLeadPayload(data: ValidatedIntakeForm, regions: Region[]) {
  const regionLabels = Object.fromEntries(regions.map((r) => [r.id, r.nameCn]));
  return {
    display_name: data.displayName,
    phone: data.phone,
    school: data.school,
    school_name: data.school === "other" ? data.schoolName : null,
    identity: data.identity,
    regions: data.school === "other" ? [] : data.regions,
    budget: data.budget,
    room_types: data.roomTypes,
    property_types: data.propertyTypes,
    move_in: data.moveIn,
    note: data.note.trim() || null,
    criterion: data.criterion ?? null,
    community_id: data.communityId ?? null,
    community_name_en: data.communityNameEn ?? null,
    unit_id: data.unitId ?? null,
    source: data.source ?? null,
    region_labels: regionLabels,
    client_meta: {
      path: typeof window !== "undefined" ? window.location.pathname + window.location.search : null,
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
    },
  };
}

const apiBase = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

export async function submitLead(
  data: ValidatedIntakeForm,
  regions: Region[],
): Promise<{ id: string; summary: string }> {
  const res = await fetch(`${apiBase}/api/leads`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(buildLeadPayload(data, regions)),
  });

  if (!res.ok) {
    let message = "提交失败，请稍后再试";
    try {
      const body = (await res.json()) as { detail?: string | { msg?: string }[] };
      if (typeof body.detail === "string") message = body.detail;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  return res.json() as Promise<{ id: string; summary: string }>;
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fallback */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
